import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import {
  PageContainer,
  PageHero,
  PageSubtitlePill,
  Section,
  EmptyState,
  Skeleton,
} from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const I = {
  Play: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  ),
  Video: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
};

export const TutorialVideos = () => {
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [data, setData] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('eng');

  const filtered = data.filter((v) => v.video_lang === selectedLanguage);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'tutorials' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({
        seo_title: 'Tutorials | ESAY',
        seo_desc: 'Walkthroughs and tutorials for navigating ESAY.',
        seo_keywords: 'esay tutorials',
      });
    });
    Axios.get('/new_apis/get-all-youtube-videos', { headers: { key } }).then((res) => {
      setData(res.data.videos || []);
      setLanguages(res.data.languages || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <PageContainer maxWidth="lg">
        <PageHero
          compact
          kicker="Learn & explore"
          title="Walkthroughs & Tutorials"
          subtitle="Step-by-step video guides to help you navigate ESAY and get the most from the platform."
        />
        <PageSubtitlePill emphasis="Learn and explore">Step-by-step guides for easy navigation.</PageSubtitlePill>

        <Section>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border-subtle bg-surface-paper p-3">
                  <Skeleton variant="rect" height={220} />
                  <div className="mt-2"><Skeleton variant="text" width="60%" height={16} /></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {languages.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-1.5 rounded-full border border-border-subtle bg-surface-paper p-1 w-fit max-w-full overflow-x-auto scrollbar-hidden">
                  {languages.map((lang) => {
                    const isActive = selectedLanguage === lang.key;
                    return (
                      <button
                        key={lang.key}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.key)}
                        className={`
                          inline-flex items-center rounded-full px-4 py-1.5 shrink-0
                          text-[11.5px] font-bold uppercase tracking-[0.12em]
                          transition-[background-color,color,box-shadow] duration-200
                          ${isActive
                            ? 'bg-amber text-ink shadow-[0_4px_12px_-4px_rgba(251,191,36,0.4)]'
                            : 'text-fg-secondary hover:bg-surface-overlay/70 hover:text-amber'}
                        `}
                      >
                        {lang.value}
                      </button>
                    );
                  })}
                </div>
              )}

              {filtered.length === 0 ? (
                <EmptyState
                  icon={<I.Video className="h-7 w-7" />}
                  title="No videos in this language yet"
                  subtitle="Try switching to another language from the tabs above."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filtered.map((video) => (
                    <article
                      key={video.video_id}
                      className="
                        group relative flex flex-col gap-3 overflow-hidden
                        rounded-2xl border border-border-subtle bg-surface-paper
                        p-3
                        transition-[border-color,transform,box-shadow] duration-200 ease-out-soft
                        hover:border-amber/40 hover:-translate-y-0.5 hover:shadow-card-hover
                      "
                    >
                      <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
                        <iframe
                          src={video.video_url.replace('watch?v=', 'embed/')}
                          title={video.video_title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 h-full w-full border-0"
                        />
                      </div>
                      <div className="flex items-start gap-2.5 px-1">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber/10 text-amber border border-amber/24 mt-0.5">
                          <I.Play className="h-3 w-3" />
                        </span>
                        <h3 className="text-[14px] font-bold tracking-[-0.01em] text-fg-primary leading-[1.4] line-clamp-2 transition-colors duration-200 group-hover:text-amber">
                          {video.video_title}
                        </h3>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </Section>
      </PageContainer>
    </>
  );
};
